// Tracks keyword-group Ids that originated from the Google Ads import flow (SIM-107), plus the
// Google Ads source linkage (account/campaign/ad group) needed by the write use case (SIM-109).
//
// The `KeywordGroups` backend contract (`ProKeywordGroup`) has no source/origin field, so this
// is a client-side, best-effort substitute for a real "imported from Google Ads" flag and a
// client-side substitute for a real link back to the originating Google Ads ad group.
//
// KNOWN LIMITATION: this data lives in the current browser's localStorage only. It is NOT synced
// across devices, browsers, or browser profiles - a list imported on one machine will not show
// the "Google Ads imported" badge (nor be eligible for Google Ads write actions) when viewed from
// another machine/browser.
const GOOGLE_ADS_IMPORTED_KEYWORD_GROUP_IDS_KEY = "googleAdsImportedKeywordGroupIds";

// The Google Ads identifiers captured at import time, needed by later write actions (add
// keyword / add negative keyword) against the originating ad group.
export interface GoogleAdsImportedLinkage {
    customerId: string;
    // Present only when the account sits under an MCC (manager account) - i.e. when the
    // account's own id differs from its resolved login/manager customer id.
    loginCustomerId?: string;
    campaignId: string;
    campaignName: string;
    adGroupId: string;
    adGroupName: string;
    adGroupResourceName: string; // e.g. "customers/123/adGroups/456"
    // The keyword texts present in the ad group at import time (SIM-113). Because the import is a
    // one-to-one copy of the ad group, this snapshot is how a later write action decides per
    // keyword whether to offer "Remove from Google Ads" (in the snapshot - was imported) or "Add
    // to Google Ads" (not in the snapshot - added to the list after import). Optional so legacy
    // entries that predate this field still load (they simply have no removable keywords).
    importedKeywords?: string[];
}

export type GoogleAdsImportedMap = Record<string /* keywordGroupId */, GoogleAdsImportedLinkage>;

// On-disk representation for a single tracked id. `null` marks an id that is known to be
// imported but has no recoverable linkage - either because it predates SIM-109 (legacy flat
// `string[]` format) or because the stored value didn't match the expected linkage shape.
type StoredLinkage = GoogleAdsImportedLinkage | null;
type StoredGoogleAdsImportedMap = Record<string, StoredLinkage>;

const isNonEmptyString = (value: unknown): value is string => typeof value === "string" && value.length > 0;

const isValidLinkage = (value: unknown): value is GoogleAdsImportedLinkage => {
    if (typeof value !== "object" || value === null) {
        return false;
    }

    const candidate = value as Record<string, unknown>;

    return (
        isNonEmptyString(candidate.customerId) &&
        (candidate.loginCustomerId === undefined || isNonEmptyString(candidate.loginCustomerId)) &&
        isNonEmptyString(candidate.campaignId) &&
        isNonEmptyString(candidate.campaignName) &&
        isNonEmptyString(candidate.adGroupId) &&
        isNonEmptyString(candidate.adGroupName) &&
        isNonEmptyString(candidate.adGroupResourceName) &&
        (candidate.importedKeywords === undefined ||
            (Array.isArray(candidate.importedKeywords) &&
                candidate.importedKeywords.every((keyword) => typeof keyword === "string")))
    );
};

// Reads the raw storage value and normalizes it into `{ id -> linkage | null }`, tolerating:
// - the legacy pre-SIM-109 format (a flat `string[]` of ids, no linkage)
// - the current format (a map of id -> linkage)
// - malformed/unexpected JSON (falls back to "nothing tracked" rather than throwing)
const readGoogleAdsImportedStorage = (): StoredGoogleAdsImportedMap => {
    if (typeof window === "undefined") {
        return {};
    }

    try {
        const rawValue = window.localStorage.getItem(GOOGLE_ADS_IMPORTED_KEYWORD_GROUP_IDS_KEY);
        if (!rawValue) {
            return {};
        }

        const parsedValue: unknown = JSON.parse(rawValue);

        if (Array.isArray(parsedValue)) {
            const map: StoredGoogleAdsImportedMap = {};
            for (const entry of parsedValue) {
                if (typeof entry === "string") {
                    map[entry] = null;
                }
            }
            return map;
        }

        if (typeof parsedValue === "object" && parsedValue !== null) {
            const map: StoredGoogleAdsImportedMap = {};
            for (const [id, value] of Object.entries(parsedValue as Record<string, unknown>)) {
                map[id] = isValidLinkage(value) ? value : null;
            }
            return map;
        }

        return {};
    } catch {
        return {};
    }
};

const writeGoogleAdsImportedStorage = (map: StoredGoogleAdsImportedMap): void => {
    if (typeof window === "undefined") {
        return;
    }

    try {
        window.localStorage.setItem(GOOGLE_ADS_IMPORTED_KEYWORD_GROUP_IDS_KEY, JSON.stringify(map));
    } catch {
        // Best-effort only - the badge/linkage are non-critical UI hints, so storage failures
        // (disabled storage, quota exceeded) shouldn't surface as errors to the user.
    }
};

export const getGoogleAdsImportedKeywordGroupIds = (): string[] => Object.keys(readGoogleAdsImportedStorage());

export const hasGoogleAdsImportedKeywordGroupId = (id: string): boolean => id in readGoogleAdsImportedStorage();

// Returns the Google Ads linkage captured at import time, or `undefined` when there is none -
// either because `id` isn't tracked at all (manual list), or because it's a legacy/malformed
// entry that predates linkage tracking.
export const getGoogleAdsLinkage = (id: string): GoogleAdsImportedLinkage | undefined =>
    readGoogleAdsImportedStorage()[id] ?? undefined;

export const addGoogleAdsImportedKeywordGroupId = (id: string, linkage?: GoogleAdsImportedLinkage): void => {
    const existingStorage = readGoogleAdsImportedStorage();
    if (id in existingStorage && !linkage) {
        return;
    }

    writeGoogleAdsImportedStorage({ ...existingStorage, [id]: linkage ?? null });
};

export const removeGoogleAdsImportedKeywordGroupId = (id: string): void => {
    const existingStorage = readGoogleAdsImportedStorage();
    if (!(id in existingStorage)) {
        return;
    }

    const { [id]: _removedEntry, ...remainingStorage } = existingStorage;
    writeGoogleAdsImportedStorage(remainingStorage);
};

const normalizeKeyword = (keyword: string): string => keyword.toLowerCase().trim();

// Keeps the stored `importedKeywords` snapshot in step with writes made to the ad group after the
// import, so the per-keyword "Add" / "Remove" choice flips once a write succeeds. No-op for ids
// without linkage.
export const updateGoogleAdsImportedKeywords = (id: string, changes: { added: string[]; removed: string[] }): void => {
    const existingStorage = readGoogleAdsImportedStorage();
    const linkage = existingStorage[id];
    if (!linkage) {
        return;
    }

    const removedKeys = new Set(changes.removed.map(normalizeKeyword));
    const addedKeys = new Set(changes.added.map(normalizeKeyword));
    const keptKeywords = (linkage.importedKeywords ?? []).filter((keyword) => {
        const key = normalizeKeyword(keyword);
        return !removedKeys.has(key) && !addedKeys.has(key);
    });

    writeGoogleAdsImportedStorage({
        ...existingStorage,
        [id]: { ...linkage, importedKeywords: [...keptKeywords, ...changes.added] },
    });
};
